import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { FolderIcon, ChevronRightIcon, ArrowLeftIcon, FileIcon, FolderOpenIcon, FileTextIcon, RefreshCwIcon, ListIcon, LayoutGridIcon, FoldersIcon, PlusIcon, MoreVerticalIcon, EditIcon, Trash2Icon, CheckIcon, XIcon, FolderPlusIcon, CopyIcon, ClipboardIcon, ScissorsIcon, ClipboardPasteIcon } from 'lucide-react';
// Componente para mostrar la vista de carpetas
export function FolderView({
  designs,
  onViewDesign
}) {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState('icons'); // 'icons' o 'list'
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [folderStructure, setFolderStructure] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [clipboardItem, setClipboardItem] = useState(null);
  const [clipboardOperation, setClipboardOperation] = useState(null); // 'copy' o 'cut'
  const newFolderInputRef = useRef(null);
  const editNameInputRef = useRef(null);
  const contextMenuRef = useRef(null);
  // Inicializar la estructura de carpetas
  useEffect(() => {
    setFolderStructure(getFolderStructure());
  }, [designs]);
  // Enfocar el input al crear o editar
  useEffect(() => {
    if (isCreatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [isCreatingFolder]);
  useEffect(() => {
    if (editingItemId && editNameInputRef.current) {
      editNameInputRef.current.focus();
      // Seleccionar todo el texto para facilitar la edición
      editNameInputRef.current.select();
    }
  }, [editingItemId]);
  // Cerrar el menú contextual al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = event => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };
    const handleEscapeKey = event => {
      if (event.key === 'Escape') {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [contextMenuRef]);
  // Agrupar diseños por categorías
  const getFolderStructure = () => {
    const structure = {
      '/': {
        type: 'folder',
        name: 'Home',
        children: ['models', 'status', 'dates', 'tags', 'custom'],
        isSystem: true
      },
      '/models': {
        type: 'folder',
        name: 'Models',
        children: [...new Set(designs.map(d => d.model.replace(/\s+/g, '-').toLowerCase()))],
        isSystem: true
      },
      '/status': {
        type: 'folder',
        name: 'Status',
        children: [...new Set(designs.map(d => d.status))],
        isSystem: true
      },
      '/dates': {
        type: 'folder',
        name: 'Dates',
        children: [...new Set(designs.map(d => {
          const date = new Date(d.date || d.lastModified || d.sharedDate);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        }))],
        isSystem: true
      },
      '/tags': {
        type: 'folder',
        name: 'Tags',
        children: [...new Set(designs.flatMap(d => d.tags || []))],
        isSystem: true
      },
      '/custom': {
        type: 'folder',
        name: 'Custom Folders',
        children: [],
        isSystem: true
      }
    };
    // Agregar diseños a las carpetas correspondientes
    designs.forEach(design => {
      // Por modelo
      const modelPath = `/models/${design.model.replace(/\s+/g, '-').toLowerCase()}`;
      if (!structure[modelPath]) {
        structure[modelPath] = {
          type: 'folder',
          name: design.model,
          children: [],
          isSystem: true
        };
      }
      structure[modelPath].children.push(design.id);
      // Por estado
      const statusPath = `/status/${design.status}`;
      if (!structure[statusPath]) {
        structure[statusPath] = {
          type: 'folder',
          name: design.status.charAt(0).toUpperCase() + design.status.slice(1),
          children: [],
          isSystem: true
        };
      }
      structure[statusPath].children.push(design.id);
      // Por fecha
      const date = new Date(design.date || design.lastModified || design.sharedDate);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const datePath = `/dates/${monthYear}`;
      if (!structure[datePath]) {
        structure[datePath] = {
          type: 'folder',
          name: `${getMonthName(date.getMonth())} ${date.getFullYear()}`,
          children: [],
          isSystem: true
        };
      }
      structure[datePath].children.push(design.id);
      // Por etiquetas
      if (design.tags) {
        design.tags.forEach(tag => {
          const tagPath = `/tags/${tag}`;
          if (!structure[tagPath]) {
            structure[tagPath] = {
              type: 'folder',
              name: tag,
              children: [],
              isSystem: true
            };
          }
          structure[tagPath].children.push(design.id);
        });
      }
    });
    return structure;
  };
  const getCurrentItems = () => {
    const currentFolder = folderStructure[currentPath];
    if (!currentFolder) return [];
    return currentFolder.children.map(childId => {
      // Si es una carpeta
      if (folderStructure[`${currentPath === '/' ? '' : currentPath}/${childId}`]) {
        return {
          id: childId,
          type: 'folder',
          name: folderStructure[`${currentPath === '/' ? '' : currentPath}/${childId}`].name,
          path: `${currentPath === '/' ? '' : currentPath}/${childId}`,
          isSystem: folderStructure[`${currentPath === '/' ? '' : currentPath}/${childId}`].isSystem
        };
      }
      // Si es un diseño
      const design = designs.find(d => d.id === childId);
      return {
        id: childId,
        type: 'file',
        name: design ? design.name : childId,
        design: design,
        currentPath: currentPath
      };
    });
  };
  const handleNavigate = path => {
    setCurrentPath(path);
    setSelectedItem(null);
    setContextMenu(null);
    setIsCreatingFolder(false);
    setEditingItemId(null);
  };
  const handleGoUp = () => {
    if (currentPath === '/') return;
    const pathParts = currentPath.split('/');
    pathParts.pop();
    const parentPath = pathParts.join('/') || '/';
    handleNavigate(parentPath);
  };
  const handleItemClick = (item, event) => {
    // Si estamos en modo de edición, no hacer nada
    if (editingItemId) return;
    if (event) {
      event.stopPropagation();
    }
    setSelectedItem(item.id);
    setContextMenu(null);
    if (item.type === 'file' && item.design && onViewDesign) {
      onViewDesign(item.design);
    }
  };
  const handleItemDoubleClick = (item, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (item.type === 'folder') {
      handleNavigate(item.path);
    } else if (item.design && onViewDesign) {
      onViewDesign(item.design);
    }
  };
  const handleContextMenu = (item, event) => {
    event.preventDefault();
    // No mostrar menú contextual para elementos del sistema
    if (item.isSystem) return;
    // Calcular la posición del menú contextual
    let x = event.clientX;
    let y = event.clientY;
    // Ajustar la posición si está cerca de los bordes de la pantalla
    const menuWidth = 200; // Ancho aproximado del menú
    const menuHeight = 160; // Altura aproximada del menú
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }
    setContextMenu({
      item,
      x,
      y
    });
    // Seleccionar el elemento al hacer click derecho
    setSelectedItem(item.id);
  };
  const handleCreateFolder = () => {
    setIsCreatingFolder(true);
    setNewFolderName('');
  };
  const saveNewFolder = () => {
    if (!newFolderName.trim()) {
      setIsCreatingFolder(false);
      return;
    }
    // Verificar si el nombre ya existe en la carpeta actual
    const existingNames = getCurrentItems().filter(item => item.type === 'folder').map(item => item.name.toLowerCase());
    if (existingNames.includes(newFolderName.toLowerCase())) {
      alert(`Ya existe una carpeta con el nombre "${newFolderName}" en esta ubicación.`);
      return;
    }
    // Generar un ID único para la carpeta
    const folderId = `folder-${Date.now()}`;
    const folderPath = `${currentPath === '/' ? '' : currentPath}/custom/${folderId}`;
    // Actualizar la estructura de carpetas
    const updatedStructure = {
      ...folderStructure
    };
    // Asegurarse de que existe la carpeta custom en la ruta actual
    const customPath = `${currentPath === '/' ? '' : currentPath}/custom`;
    if (!updatedStructure[customPath]) {
      updatedStructure[customPath] = {
        type: 'folder',
        name: 'Custom Folders',
        children: [],
        isSystem: true
      };
      // Agregar custom a los hijos de la carpeta actual si no existe
      if (!updatedStructure[currentPath].children.includes('custom')) {
        updatedStructure[currentPath].children.push('custom');
      }
    }
    // Crear la nueva carpeta
    updatedStructure[folderPath] = {
      type: 'folder',
      name: newFolderName,
      children: [],
      isSystem: false
    };
    // Agregar la nueva carpeta a los hijos de la carpeta custom
    updatedStructure[customPath].children.push(folderId);
    setFolderStructure(updatedStructure);
    setIsCreatingFolder(false);
    // Si estamos en la raíz, navegar a la carpeta custom
    if (currentPath === '/') {
      handleNavigate('/custom');
    }
  };
  const cancelNewFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName('');
  };
  const handleEditItem = item => {
    setEditingItemId(item.id);
    setEditingName(item.name);
    setContextMenu(null);
  };
  const saveEditedName = () => {
    if (!editingName.trim() || !editingItemId) {
      setEditingItemId(null);
      return;
    }
    const items = getCurrentItems();
    const item = items.find(i => i.id === editingItemId);
    if (!item) {
      setEditingItemId(null);
      setEditingName('');
      return;
    }
    const updatedStructure = {
      ...folderStructure
    };
    if (item.type === 'folder') {
      // Verificar si el nombre ya existe en la misma carpeta
      const siblings = getCurrentItems();
      const nameExists = siblings.some(sibling => sibling.id !== item.id && sibling.name.toLowerCase() === editingName.toLowerCase() && sibling.type === 'folder');
      if (nameExists) {
        alert(`Ya existe una carpeta con el nombre "${editingName}" en esta ubicación.`);
        return;
      }
      // Actualizar el nombre de la carpeta
      updatedStructure[item.path].name = editingName;
    } else if (item.type === 'file' && item.design) {
      // Para archivos, podríamos implementar la lógica para actualizar el nombre del diseño
      // pero esto requeriría una función de callback desde el componente padre
      console.log(`Renombrando archivo: ${item.name} a ${editingName}`);
      // Esta funcionalidad requeriría actualizar el objeto design original
      // onRenameDesign(item.design.id, editingName)
    }
    setFolderStructure(updatedStructure);
    setEditingItemId(null);
    setEditingName('');
  };
  const cancelEdit = () => {
    setEditingItemId(null);
    setEditingName('');
  };
  const handleDeleteItem = item => {
    if (item.type === 'folder') {
      // Obtener la carpeta padre
      const pathParts = item.path.split('/');
      const itemId = pathParts.pop();
      const parentPath = pathParts.join('/') || '/';
      // Confirmar eliminación
      if (!confirm(`¿Estás seguro de que deseas eliminar la carpeta "${item.name}"?`)) {
        return;
      }
      // Actualizar la estructura de carpetas
      const updatedStructure = {
        ...folderStructure
      };
      // Eliminar la carpeta de los hijos de la carpeta padre
      updatedStructure[parentPath].children = updatedStructure[parentPath].children.filter(childId => childId !== itemId);
      // Eliminar la carpeta
      delete updatedStructure[item.path];
      setFolderStructure(updatedStructure);
    } else if (item.type === 'file') {
      // Confirmar eliminación
      if (!confirm(`¿Estás seguro de que deseas eliminar el archivo "${item.name}"?`)) {
        return;
      }
      // Actualizar la estructura de carpetas
      const updatedStructure = {
        ...folderStructure
      };
      // Eliminar el archivo de los hijos de la carpeta actual
      updatedStructure[currentPath].children = updatedStructure[currentPath].children.filter(childId => childId !== item.id);
      setFolderStructure(updatedStructure);
    }
    setContextMenu(null);
  };
  // Funciones para drag and drop
  const handleDragStart = (item, event) => {
    // No permitir arrastrar carpetas del sistema
    if (item.isSystem) {
      event.preventDefault();
      return;
    }
    // Configurar los datos para la transferencia
    event.dataTransfer.setData('application/json', JSON.stringify({
      id: item.id,
      type: item.type,
      path: item.path,
      currentPath: currentPath
    }));
    // Establecer el efecto de arrastre
    event.dataTransfer.effectAllowed = 'move';
    // Guardar el elemento arrastrado en el estado
    setDraggedItem(item);
    // Si es un archivo, añadir una clase para estilos de arrastre
    if (event.currentTarget) {
      setTimeout(() => {
        event.currentTarget.classList.add('opacity-50');
      }, 0);
    }
  };
  const handleDragEnd = event => {
    // Limpiar el estado y los estilos
    setDraggedItem(null);
    setDropTarget(null);
    if (event.currentTarget) {
      event.currentTarget.classList.remove('opacity-50');
    }
    // Eliminar las clases de estilo de todos los elementos potenciales de destino
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
  };
  const handleDragOver = (item, event) => {
    // Prevenir el comportamiento predeterminado para permitir el drop
    event.preventDefault();
    // Solo permitir soltar en carpetas que no sean el elemento arrastrado
    if (item.type === 'folder' && draggedItem && draggedItem.id !== item.id) {
      event.dataTransfer.dropEffect = 'move';
      setDropTarget(item);
      // Añadir clase para estilos visuales
      if (event.currentTarget) {
        event.currentTarget.classList.add('drag-over');
      }
    } else {
      event.dataTransfer.dropEffect = 'none';
    }
  };
  const handleDragEnter = (item, event) => {
    // Prevenir el comportamiento predeterminado
    event.preventDefault();
    // Solo resaltar carpetas como destino potencial
    if (item.type === 'folder' && draggedItem && draggedItem.id !== item.id) {
      setDropTarget(item);
      // Añadir clase para estilos visuales
      if (event.currentTarget) {
        event.currentTarget.classList.add('drag-over');
      }
    }
  };
  const handleDragLeave = event => {
    // Eliminar la clase de estilo cuando el cursor sale del elemento
    if (event.currentTarget) {
      event.currentTarget.classList.remove('drag-over');
    }
    setDropTarget(null);
  };
  const handleDrop = (targetItem, event) => {
    // Prevenir el comportamiento predeterminado
    event.preventDefault();
    // Eliminar la clase de estilo
    if (event.currentTarget) {
      event.currentTarget.classList.remove('drag-over');
    }
    // Solo procesar si el destino es una carpeta
    if (targetItem.type !== 'folder' || !draggedItem) {
      setDraggedItem(null);
      setDropTarget(null);
      return;
    }
    // Obtener los datos del elemento arrastrado
    const data = JSON.parse(event.dataTransfer.getData('application/json'));
    // No permitir soltar en la misma carpeta de origen
    if (data.currentPath === targetItem.path) {
      setDraggedItem(null);
      setDropTarget(null);
      return;
    }
    // Actualizar la estructura de carpetas
    const updatedStructure = {
      ...folderStructure
    };
    if (data.type === 'file') {
      // Mover un archivo
      // 1. Eliminar el archivo de la carpeta actual
      updatedStructure[data.currentPath].children = updatedStructure[data.currentPath].children.filter(childId => childId !== data.id);
      // 2. Añadir el archivo a la carpeta de destino
      updatedStructure[targetItem.path].children.push(data.id);
    } else if (data.type === 'folder') {
      // Mover una carpeta (más complejo)
      // 1. Obtener la carpeta padre del elemento arrastrado
      const pathParts = data.path.split('/');
      const itemId = pathParts.pop();
      const parentPath = pathParts.join('/') || '/';
      // 2. Eliminar la carpeta de la lista de hijos de la carpeta padre
      updatedStructure[parentPath].children = updatedStructure[parentPath].children.filter(childId => childId !== itemId);
      // 3. Añadir la carpeta a la lista de hijos de la carpeta de destino
      updatedStructure[targetItem.path].children.push(itemId);
      // 4. Actualizar la ruta de la carpeta y todas sus subcarpetas recursivamente
      const updateFolderPath = (oldPath, newParentPath) => {
        const folder = updatedStructure[oldPath];
        if (!folder) return;
        const folderName = oldPath.split('/').pop();
        const newPath = `${newParentPath}/${folderName}`;
        // Crear la carpeta en la nueva ubicación
        updatedStructure[newPath] = {
          ...folder
        };
        // Eliminar la carpeta de la ubicación antigua
        delete updatedStructure[oldPath];
        // Actualizar las rutas de todas las subcarpetas
        folder.children.forEach(childId => {
          const childPath = `${oldPath}/${childId}`;
          if (updatedStructure[childPath] && updatedStructure[childPath].type === 'folder') {
            updateFolderPath(childPath, newPath);
          }
        });
      };
      updateFolderPath(data.path, targetItem.path);
    }
    // Actualizar el estado
    setFolderStructure(updatedStructure);
    setDraggedItem(null);
    setDropTarget(null);
  };
  // Funciones para copiar y pegar
  const handleCopyItem = item => {
    setClipboardItem({
      ...item,
      currentPath: currentPath
    });
    setClipboardOperation('copy');
    setContextMenu(null);
  };
  const handleCutItem = item => {
    setClipboardItem({
      ...item,
      currentPath: currentPath
    });
    setClipboardOperation('cut');
    setContextMenu(null);
  };
  const handlePasteItem = () => {
    if (!clipboardItem) return;
    const updatedStructure = {
      ...folderStructure
    };
    if (clipboardItem.type === 'file') {
      // Copiar/Cortar un archivo
      if (clipboardOperation === 'cut') {
        // Obtener la carpeta de origen
        const sourcePath = clipboardItem.currentPath || currentPath;
        // Eliminar el archivo de la carpeta de origen
        updatedStructure[sourcePath].children = updatedStructure[sourcePath].children.filter(childId => childId !== clipboardItem.id);
      }
      // Añadir el archivo a la carpeta actual
      if (!updatedStructure[currentPath].children.includes(clipboardItem.id)) {
        updatedStructure[currentPath].children.push(clipboardItem.id);
      }
    } else if (clipboardItem.type === 'folder') {
      // Copiar/Cortar una carpeta
      const originalPath = clipboardItem.path;
      if (clipboardOperation === 'copy') {
        // Generar un nuevo ID para la carpeta copiada
        const newFolderId = `folder-${Date.now()}`;
        // Crear una copia de la carpeta original
        const originalFolder = updatedStructure[originalPath];
        // Crear la nueva carpeta en la ubicación actual
        const newPath = `${currentPath === '/' ? '' : currentPath}/${newFolderId}`;
        // Función recursiva para copiar una carpeta y sus contenidos
        const copyFolder = (sourcePath, targetPath, newId) => {
          const sourceFolder = updatedStructure[sourcePath];
          if (!sourceFolder) return;
          // Crear la carpeta en la nueva ubicación
          updatedStructure[targetPath] = {
            type: 'folder',
            name: sourceFolder.name + ' (Copy)',
            children: [...sourceFolder.children],
            isSystem: false
          };
          // Añadir la nueva carpeta a los hijos de la carpeta actual
          if (!updatedStructure[currentPath].children.includes(newId)) {
            updatedStructure[currentPath].children.push(newId);
          }
          // Copiar recursivamente todas las subcarpetas
          sourceFolder.children.forEach(childId => {
            const childPath = `${sourcePath}/${childId}`;
            if (updatedStructure[childPath] && updatedStructure[childPath].type === 'folder') {
              const newChildId = `folder-${Date.now()}-${childId}`;
              const newChildPath = `${targetPath}/${newChildId}`;
              // Reemplazar el ID del hijo en la lista de hijos
              const childIndex = updatedStructure[targetPath].children.indexOf(childId);
              if (childIndex !== -1) {
                updatedStructure[targetPath].children[childIndex] = newChildId;
              }
              copyFolder(childPath, newChildPath, newChildId);
            }
          });
        };
        copyFolder(originalPath, newPath, newFolderId);
      } else if (clipboardOperation === 'cut') {
        // Obtener la carpeta padre del elemento cortado
        const pathParts = originalPath.split('/');
        const itemId = pathParts.pop();
        const parentPath = pathParts.join('/') || '/';
        // No permitir cortar y pegar en la misma carpeta
        if (parentPath === currentPath) {
          setClipboardItem(null);
          setClipboardOperation(null);
          return;
        }
        // Eliminar la carpeta de la lista de hijos de la carpeta padre
        updatedStructure[parentPath].children = updatedStructure[parentPath].children.filter(childId => childId !== itemId);
        // Añadir la carpeta a la lista de hijos de la carpeta actual
        if (!updatedStructure[currentPath].children.includes(itemId)) {
          updatedStructure[currentPath].children.push(itemId);
        }
        // Actualizar la ruta de la carpeta y todas sus subcarpetas recursivamente
        const updateFolderPath = (oldPath, newParentPath) => {
          const folder = updatedStructure[oldPath];
          if (!folder) return;
          const folderName = oldPath.split('/').pop();
          const newPath = `${newParentPath}/${folderName}`;
          // Crear la carpeta en la nueva ubicación
          updatedStructure[newPath] = {
            ...folder
          };
          // Eliminar la carpeta de la ubicación antigua
          delete updatedStructure[oldPath];
          // Actualizar las rutas de todas las subcarpetas
          folder.children.forEach(childId => {
            const childPath = `${oldPath}/${childId}`;
            if (updatedStructure[childPath] && updatedStructure[childPath].type === 'folder') {
              updateFolderPath(childPath, newPath);
            }
          });
        };
        updateFolderPath(originalPath, currentPath);
      }
    }
    // Actualizar el estado
    setFolderStructure(updatedStructure);
    // Si la operación fue cortar, limpiar el portapapeles
    if (clipboardOperation === 'cut') {
      setClipboardItem(null);
      setClipboardOperation(null);
    }
  };
  const getBreadcrumbs = () => {
    if (currentPath === '/') {
      return [{
        name: 'Home',
        path: '/'
      }];
    }
    const parts = currentPath.split('/').filter(Boolean);
    let breadcrumbs = [{
      name: 'Home',
      path: '/'
    }];
    let currentBuildPath = '';
    parts.forEach(part => {
      currentBuildPath += `/${part}`;
      const folderInfo = folderStructure[currentBuildPath];
      breadcrumbs.push({
        name: folderInfo ? folderInfo.name : part,
        path: currentBuildPath
      });
    });
    return breadcrumbs;
  };
  const handleKeyDown = e => {
    // Manejar teclas de acceso rápido
    if (e.key === 'F2' && selectedItem) {
      // Iniciar edición con F2
      const item = getCurrentItems().find(item => item.id === selectedItem);
      if (item && !item.isSystem) {
        handleEditItem(item);
      }
      e.preventDefault();
    } else if (e.key === 'Delete' && selectedItem) {
      // Eliminar con Delete
      const item = getCurrentItems().find(item => item.id === selectedItem);
      if (item && !item.isSystem) {
        handleDeleteItem(item);
      }
      e.preventDefault();
    } else if (e.ctrlKey && e.key === 'c' && selectedItem) {
      // Copiar con Ctrl+C
      const item = getCurrentItems().find(item => item.id === selectedItem);
      if (item && !item.isSystem) {
        handleCopyItem(item);
      }
      e.preventDefault();
    } else if (e.ctrlKey && e.key === 'x' && selectedItem) {
      // Cortar con Ctrl+X
      const item = getCurrentItems().find(item => item.id === selectedItem);
      if (item && !item.isSystem) {
        handleCutItem(item);
      }
      e.preventDefault();
    } else if (e.ctrlKey && e.key === 'v' && clipboardItem) {
      // Pegar con Ctrl+V
      handlePasteItem();
      e.preventDefault();
    }
  };
  // Agregar event listener para teclas de acceso rápido
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItem, clipboardItem, currentPath]);
  const items = getCurrentItems();
  const breadcrumbs = getBreadcrumbs();
  return <div className="h-full flex flex-col" data-id="element-2774">
      {/* Barra de navegación de carpetas */}
      <div className="bg-white border-b border-gray-200 p-2 flex items-center space-x-2" data-id="element-2775">
        <button onClick={handleGoUp} disabled={currentPath === '/'} className={`p-1.5 rounded ${currentPath === '/' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2776">
          <ArrowLeftIcon size={18} data-id="element-2777" />
        </button>
        <div className="flex items-center overflow-x-auto space-x-1 flex-1" data-id="element-2778">
          {breadcrumbs.map((crumb, index) => <Fragment key={crumb.path} data-id="element-2779">
              {index > 0 && <ChevronRightIcon size={14} className="text-gray-400" data-id="element-2780" />}
              <button onClick={() => handleNavigate(crumb.path)} className={`px-2 py-1 rounded ${currentPath === crumb.path ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-2781">
                {index === 0 ? <div className="flex items-center" data-id="element-2782">
                    <FolderIcon size={16} className="mr-1 text-blue-500" data-id="element-2783" />
                    {crumb.name}
                  </div> : crumb.name}
              </button>
            </Fragment>)}
        </div>
        <div className="flex items-center space-x-1" data-id="element-2784">
          <button onClick={() => setViewMode('icons')} className={`p-1.5 rounded ${viewMode === 'icons' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} title="Vista de iconos" data-id="element-2785">
            <LayoutGridIcon size={18} data-id="element-2786" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} title="Vista de lista" data-id="element-2787">
            <ListIcon size={18} data-id="element-2788" />
          </button>
          <button onClick={handleCreateFolder} className="p-1.5 rounded text-gray-700 hover:bg-gray-100" title="Nueva carpeta" data-id="element-2789">
            <FolderPlusIcon size={18} data-id="element-2790" />
          </button>
          {clipboardItem && <button onClick={handlePasteItem} className="p-1.5 rounded text-gray-700 hover:bg-gray-100" title={`Pegar ${clipboardItem.type === 'folder' ? 'carpeta' : 'archivo'}`} data-id="element-2791">
              <ClipboardPasteIcon size={18} data-id="element-2792" />
            </button>}
          <button className="p-1.5 rounded text-gray-700 hover:bg-gray-100" title="Actualizar" data-id="element-2793">
            <RefreshCwIcon size={18} data-id="element-2794" />
          </button>
        </div>
      </div>
      {/* Contenido de la carpeta */}
      <div className="flex-1 overflow-auto p-2 bg-white" onClick={() => setSelectedItem(null)} data-id="element-2795">
        {items.length === 0 && !isCreatingFolder ? <div className="flex flex-col items-center justify-center h-full text-gray-500" data-id="element-2796">
            <FolderOpenIcon size={48} className="mb-2 text-gray-400" data-id="element-2797" />
            <p data-id="element-2798">Esta carpeta está vacía</p>
            <button onClick={handleCreateFolder} className="mt-4 flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200" data-id="element-2799">
              <FolderPlusIcon size={16} className="mr-2" data-id="element-2800" />
              Crear carpeta
            </button>
            {clipboardItem && <button onClick={handlePasteItem} className="mt-2 flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200" data-id="element-2801">
                <ClipboardPasteIcon size={16} className="mr-2" data-id="element-2802" />
                Pegar {clipboardItem.type === 'folder' ? 'carpeta' : 'archivo'}
              </button>}
          </div> : <>
            {isCreatingFolder && <div className="mb-4 p-3 rounded-lg border border-blue-300 bg-blue-50" data-id="element-2803">
                <div className="flex items-center" data-id="element-2804">
                  <FolderIcon size={20} className="mr-2 text-blue-500" data-id="element-2805" />
                  <input ref={newFolderInputRef} type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nombre de la carpeta" className="flex-1 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" onKeyDown={e => {
              if (e.key === 'Enter') saveNewFolder();
              if (e.key === 'Escape') cancelNewFolder();
            }} data-id="element-2806" />
                  <button onClick={saveNewFolder} className="ml-2 p-1.5 rounded text-green-700 hover:bg-green-100" title="Guardar" data-id="element-2807">
                    <CheckIcon size={18} data-id="element-2808" />
                  </button>
                  <button onClick={cancelNewFolder} className="ml-1 p-1.5 rounded text-red-700 hover:bg-red-100" title="Cancelar" data-id="element-2809">
                    <XIcon size={18} data-id="element-2810" />
                  </button>
                </div>
              </div>}
            {viewMode === 'icons' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-id="element-2811">
                {items.map(item => <div key={item.id} onClick={e => handleItemClick(item, e)} onDoubleClick={e => handleItemDoubleClick(item, e)} onContextMenu={e => handleContextMenu(item, e)} draggable={!item.isSystem} onDragStart={e => handleDragStart(item, e)} onDragEnd={handleDragEnd} onDragOver={e => handleDragOver(item, e)} onDragEnter={e => handleDragEnter(item, e)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(item, e)} className={`p-3 rounded-lg border ${selectedItem === item.id ? 'border-blue-500 bg-blue-50' : dropTarget && dropTarget.id === item.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} cursor-pointer transition-colors relative ${item.type === 'folder' ? 'folder-item' : 'file-item'} ${clipboardItem && clipboardOperation === 'cut' && clipboardItem.id === item.id ? 'opacity-50' : ''}`} data-id="element-2812">
                    {editingItemId === item.id ? <div className="flex flex-col items-center" data-id="element-2813">
                        {item.type === 'folder' ? <FolderIcon size={48} className="mb-2 text-yellow-500" data-id="element-2814" /> : <FileTextIcon size={48} className="mb-2 text-blue-500" data-id="element-2815" />}
                        <input ref={editNameInputRef} type="text" value={editingName} onChange={e => setEditingName(e.target.value)} className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-center" onKeyDown={e => {
                if (e.key === 'Enter') saveEditedName();
                if (e.key === 'Escape') cancelEdit();
                e.stopPropagation();
              }} onClick={e => e.stopPropagation()} data-id="element-2816" />
                        <div className="mt-2 flex justify-center" data-id="element-2817">
                          <button onClick={e => {
                  e.stopPropagation();
                  saveEditedName();
                }} className="p-1 rounded text-green-700 hover:bg-green-100 mr-1" title="Guardar" data-id="element-2818">
                            <CheckIcon size={16} data-id="element-2819" />
                          </button>
                          <button onClick={e => {
                  e.stopPropagation();
                  cancelEdit();
                }} className="p-1 rounded text-red-700 hover:bg-red-100" title="Cancelar" data-id="element-2820">
                            <XIcon size={16} data-id="element-2821" />
                          </button>
                        </div>
                      </div> : <div className="flex flex-col items-center text-center" data-id="element-2822">
                        {item.type === 'folder' ? <FolderIcon size={48} className="mb-2 text-yellow-500" data-id="element-2823" /> : <FileTextIcon size={48} className="mb-2 text-blue-500" data-id="element-2824" />}
                        <span className="text-sm font-medium text-gray-700 break-all line-clamp-2" data-id="element-2825">
                          {item.name}
                        </span>
                        {item.type === 'file' && item.design && <span className="text-xs text-gray-500 mt-1" data-id="element-2826">
                            {item.design.model}
                          </span>}
                      </div>}
                    {!item.isSystem && selectedItem === item.id && !editingItemId && <div className="absolute top-2 right-2 bg-white rounded-md shadow-sm border border-gray-200" data-id="element-2827">
                          <button onClick={e => {
                e.stopPropagation();
                handleEditItem(item);
              }} className="p-1 text-gray-700 hover:bg-gray-100" title="Editar" data-id="element-2828">
                            <EditIcon size={14} data-id="element-2829" />
                          </button>
                          <button onClick={e => {
                e.stopPropagation();
                handleCopyItem(item);
              }} className="p-1 text-gray-700 hover:bg-gray-100" title="Copiar" data-id="element-2830">
                            <CopyIcon size={14} data-id="element-2831" />
                          </button>
                          <button onClick={e => {
                e.stopPropagation();
                handleCutItem(item);
              }} className="p-1 text-gray-700 hover:bg-gray-100" title="Cortar" data-id="element-2832">
                            <ScissorsIcon size={14} data-id="element-2833" />
                          </button>
                          <button onClick={e => {
                e.stopPropagation();
                handleDeleteItem(item);
              }} className="p-1 text-gray-700 hover:bg-gray-100 hover:text-red-600" title="Eliminar" data-id="element-2834">
                            <Trash2Icon size={14} data-id="element-2835" />
                          </button>
                        </div>}
                    {/* Indicador de arrastre */}
                    {draggedItem && draggedItem.id === item.id && <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg border-2 border-blue-500 pointer-events-none" data-id="element-2836"></div>}
                    {/* Indicador de destino de soltar */}
                    {dropTarget && dropTarget.id === item.id && item.type === 'folder' && <div className="absolute inset-0 bg-green-500 bg-opacity-20 rounded-lg border-2 border-green-500 pointer-events-none" data-id="element-2837"></div>}
                  </div>)}
              </div> : <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-2838">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2839">
                  <thead className="bg-gray-50" data-id="element-2840">
                    <tr data-id="element-2841">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2842">
                        Nombre
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2843">
                        Tipo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2844">
                        Fecha de modificación
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2845">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2846">
                    {items.map(item => <tr key={item.id} onClick={e => handleItemClick(item, e)} onDoubleClick={e => handleItemDoubleClick(item, e)} onContextMenu={e => handleContextMenu(item, e)} draggable={!item.isSystem} onDragStart={e => handleDragStart(item, e)} onDragEnd={handleDragEnd} onDragOver={e => handleDragOver(item, e)} onDragEnter={e => handleDragEnter(item, e)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(item, e)} className={`${selectedItem === item.id ? 'bg-blue-50' : dropTarget && dropTarget.id === item.id ? 'bg-green-50' : 'hover:bg-gray-50'} cursor-pointer ${item.type === 'folder' ? 'folder-item' : 'file-item'} ${clipboardItem && clipboardOperation === 'cut' && clipboardItem.id === item.id ? 'opacity-50' : ''}`} data-id="element-2847">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2848">
                          <div className="flex items-center" data-id="element-2849">
                            {item.type === 'folder' ? <FolderIcon className="flex-shrink-0 h-5 w-5 text-yellow-500" data-id="element-2850" /> : <FileTextIcon className="flex-shrink-0 h-5 w-5 text-blue-500" data-id="element-2851" />}
                            <div className="ml-4" data-id="element-2852">
                              {editingItemId === item.id ? <div className="flex items-center" data-id="element-2853">
                                  <input ref={editNameInputRef} type="text" value={editingName} onChange={e => setEditingName(e.target.value)} className="px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" onKeyDown={e => {
                          if (e.key === 'Enter') saveEditedName();
                          if (e.key === 'Escape') cancelEdit();
                          e.stopPropagation();
                        }} onClick={e => e.stopPropagation()} data-id="element-2854" />
                                  <button onClick={e => {
                          e.stopPropagation();
                          saveEditedName();
                        }} className="ml-2 p-1 rounded text-green-700 hover:bg-green-100" title="Guardar" data-id="element-2855">
                                    <CheckIcon size={16} data-id="element-2856" />
                                  </button>
                                  <button onClick={e => {
                          e.stopPropagation();
                          cancelEdit();
                        }} className="ml-1 p-1 rounded text-red-700 hover:bg-red-100" title="Cancelar" data-id="element-2857">
                                    <XIcon size={16} data-id="element-2858" />
                                  </button>
                                </div> : <div className="text-sm font-medium text-gray-900" data-id="element-2859">
                                  {item.name}
                                </div>}
                              {item.type === 'file' && item.design && <div className="text-xs text-gray-500" data-id="element-2860">
                                  {item.design.model}
                                </div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2861">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-2862">
                            {item.type === 'folder' ? 'Carpeta' : 'Archivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2863">
                          {item.design?.date || item.design?.lastModified || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2864">
                          {!item.isSystem && <div className="flex justify-end" data-id="element-2865">
                              <button onClick={e => {
                      e.stopPropagation();
                      handleEditItem(item);
                    }} className="text-indigo-600 hover:text-indigo-900 ml-2" title="Editar" data-id="element-2866">
                                <EditIcon size={16} data-id="element-2867" />
                              </button>
                              <button onClick={e => {
                      e.stopPropagation();
                      handleCopyItem(item);
                    }} className="text-indigo-600 hover:text-indigo-900 ml-2" title="Copiar" data-id="element-2868">
                                <CopyIcon size={16} data-id="element-2869" />
                              </button>
                              <button onClick={e => {
                      e.stopPropagation();
                      handleCutItem(item);
                    }} className="text-indigo-600 hover:text-indigo-900 ml-2" title="Cortar" data-id="element-2870">
                                <ScissorsIcon size={16} data-id="element-2871" />
                              </button>
                              <button onClick={e => {
                      e.stopPropagation();
                      handleDeleteItem(item);
                    }} className="text-red-600 hover:text-red-900 ml-2" title="Eliminar" data-id="element-2872">
                                <Trash2Icon size={16} data-id="element-2873" />
                              </button>
                            </div>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </>}
      </div>
      {/* Menú contextual */}
      {contextMenu && <div ref={contextMenuRef} className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 animate-fade-in" style={{
      left: `${contextMenu.x}px`,
      top: `${contextMenu.y}px`,
      minWidth: '180px'
    }} onClick={e => e.stopPropagation()} role="menu" aria-orientation="vertical" aria-labelledby="context-menu-button" data-id="element-2874">
          <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors" onClick={() => handleEditItem(contextMenu.item)} role="menuitem" data-id="element-2875">
            <EditIcon size={14} className="mr-2" data-id="element-2876" />
            Editar
          </button>
          <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors" onClick={() => handleCopyItem(contextMenu.item)} role="menuitem" data-id="element-2877">
            <CopyIcon size={14} className="mr-2" data-id="element-2878" />
            Copiar
          </button>
          <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors" onClick={() => handleCutItem(contextMenu.item)} role="menuitem" data-id="element-2879">
            <ScissorsIcon size={14} className="mr-2" data-id="element-2880" />
            Cortar
          </button>
          <div className="border-t border-gray-200 my-1" data-id="element-2881"></div>
          <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors" onClick={() => handleDeleteItem(contextMenu.item)} role="menuitem" data-id="element-2882">
            <Trash2Icon size={14} className="mr-2" data-id="element-2883" />
            Eliminar
          </button>
        </div>}
      {/* Barra de estado */}
      <div className="bg-gray-100 border-t border-gray-200 px-3 py-1.5 text-xs text-gray-600 flex justify-between items-center" data-id="element-2884">
        <span data-id="element-2885">{items.length} elementos</span>
        <span data-id="element-2886">
          {viewMode === 'icons' ? 'Vista de iconos' : 'Vista de lista'} •
          {selectedItem ? ' 1 elemento seleccionado' : ' Ningún elemento seleccionado'}
          {clipboardItem && <>
              {' • '}
              <span className="text-blue-600" data-id="element-2887">
                {clipboardOperation === 'copy' ? 'Copiado: ' : 'Cortado: '}
                {clipboardItem.name}
              </span>
            </>}
        </span>
      </div>
      {/* Estilos para drag and drop y animaciones */}
      <style jsx global data-id="element-2888">{`
        .drag-over {
          background-color: rgba(16, 185, 129, 0.1);
          border-color: rgb(16, 185, 129);
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
        }
        .opacity-50 {
          opacity: 0.5;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.1s ease-out;
        }
      `}</style>
    </div>;
}
// Función auxiliar para obtener el nombre del mes
function getMonthName(monthIndex) {
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return months[monthIndex];
}